import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Button, Chip, Tabs, Tab, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, InputAdornment, Snackbar, Alert, CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon,
  ViewList as ListIcon,
  CheckCircleOutline as ActiveIcon,
  AccessTime as UpcomingIcon,
  LocalOfferOutlined as TypeIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import HeaderBannerPage from "../../components/HeaderBannerPage";
import { fetchHolidays, addHoliday, updateHoliday, deleteHoliday } from "../../actions/Leave/HolidayAction";


const HOLIDAY_TYPES = ["Public", "Company"];

const TYPE_STYLE = {
  Public: { color: "#ea580c", bg: "#ffedd5" },
  Company: { color: "#4f46e5", bg: "#e0e7ff" },
  Other: { color: "#64748b", bg: "#f1f5f9" },
};
const typeStyle = (t) => TYPE_STYLE[t] || TYPE_STYLE.Other;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const NAVY = "#142c54";
const cardSx = {
  borderRadius: "12px",
  border: "1px solid #eef0f4",
  boxShadow: "0px 2px 8px rgba(20, 44, 84, 0.06)",
  backgroundColor: "#fff",
};

//helpers

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromKey = (k) => new Date(`${k}T00:00:00`);
const fmtDate = (k) =>
  fromKey(k).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const buildMonthMatrix = (year, month) => {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday first
  const start = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const EMPTY_FORM = { id: null, name: "", date: "", type: "", active: true, remarks: "" };

//small blocks

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2, backgroundColor: bg, borderColor: `${color}33` }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>{label}</Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
      </Box>
      <Icon sx={{ fontSize: 30, color, opacity: 0.8 }} />
    </Box>
  </Paper>
);

//page

const HolidayCalendar = () => {
  const dispatch = useDispatch();
  const { data: holidays = [], loading, error } = useSelector((s) => s.leaveHolidays) || {};
  const userId = useSelector((s) => s.auth?.serviceNo);
  const refreshing = loading;
  const [saving, setSaving] = useState(false);

  const [tab, setTab] = useState(0);
  const now = new Date();
  const todayKey = toKey(now);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedKey, setSelectedKey] = useState(todayKey);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  useEffect(() => { dispatch(fetchHolidays()); }, [dispatch]);
  useEffect(() => { if (error) notify(error, "error"); }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(() => { dispatch(fetchHolidays()); }, [dispatch]);

  /*  Derived data  */
  const stats = useMemo(() => ({
    total: holidays.length,
    active: holidays.filter((h) => h.active).length,
    upcoming: holidays.filter((h) => h.active && h.date >= todayKey).length,
    types: new Set(holidays.map((h) => h.type)).size,
  }), [holidays, todayKey]);

  const holidaysByDate = useMemo(() => {
    const map = {};
    holidays.filter((h) => h.active).forEach((h) => {
      (map[h.date] = map[h.date] || []).push(h);
    });
    return map;
  }, [holidays]);

  const monthDays = useMemo(() => buildMonthMatrix(viewYear, viewMonth), [viewYear, viewMonth]);

  const monthHolidays = useMemo(
    () =>
      holidays
        .filter((h) => {
          const d = fromKey(h.date);
          return h.active && d.getFullYear() === viewYear && d.getMonth() === viewMonth;
        })
        .sort((a, b) => a.date.localeCompare(b.date)),
    [holidays, viewYear, viewMonth]
  );

  const selectedHolidays = holidaysByDate[selectedKey] || [];

  const tableRows = useMemo(
    () =>
      holidays
        .filter((h) => {
          if (typeFilter !== "all" && h.type !== typeFilter) return false;
          if (statusFilter === "active" && !h.active) return false;
          if (statusFilter === "inactive" && h.active) return false;
          if (search.trim() && !h.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
          return true;
        })
        .sort((a, b) => a.date.localeCompare(b.date)),
    [holidays, search, typeFilter, statusFilter]
  );

  /*  Calendar navigation */
  const goMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  /*  Add / edit / delete  */
  const openAdd = () => {
    setForm({ ...EMPTY_FORM, date: selectedKey });
    setFormErrors({});
    setDialogOpen(true);
  };
  const openEdit = (h) => {
    setForm({ ...h });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.date) errs.date = "Date is required";
    if (!form.name.trim()) errs.name = "Holiday name is required";
    if (!form.type) errs.type = "Holiday type is required";
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    if (!userId) return notify("Your user session was not found. Please sign in again.", "error");

    setSaving(true);
    const res = await dispatch(
      (form.id ? updateHoliday : addHoliday)({
        id: form.id,
        date: form.date,
        name: form.name.trim(),
        type: form.type,
        active: !!form.active,
        remarks: (form.remarks || "").trim(),
        userId,
      })
    );
    setSaving(false);
    if (!res.ok) return notify(res.message, "error");

    notify(form.id ? "Holiday updated" : "Holiday added");
    // jump the calendar to the saved date
    const d = fromKey(form.date);
    setSelectedKey(form.date);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    setSaving(true);
    const res = await dispatch(deleteHoliday(deleteTarget.id));
    setSaving(false);
    if (!res.ok) return notify(res.message, "error");
    setDeleteTarget(null);
    notify("Holiday deleted");
  };

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* Render  */
  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <HeaderBannerPage />
      <Box sx={{ maxWidth: 1400, mx: "auto", mt: 4, px: 2, pb: 4 }}>
        {/* Banner */}
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: "12px",
            background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
            color: "#fff", display: "flex", justifyContent: "space-between",
            alignItems: "center", flexWrap: "wrap", gap: 2,
            boxShadow: "0px 4px 12px rgba(30, 1, 83, 0.15)",
          }}
        >
          <Box>
            <Chip
              label="LEAVE MANAGEMENT"
              size="small"
              sx={{ fontWeight: "bold", mb: 1, fontSize: "11px", letterSpacing: "0.5px", backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
              Holiday Calendar
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "13px" }}>
              Manage company holidays and public holidays
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              startIcon={<RefreshIcon sx={refreshing ? { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } : {}} />}
              sx={{
                height: 38, px: 2.5, borderRadius: "8px", fontWeight: "bold", textTransform: "none",
                color: "#fff", border: "1px solid rgba(255,255,255,0.5)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.6)" },
              }}
            >
              Refresh
            </Button>
            <Button
              onClick={openAdd}
              startIcon={<AddIcon />}
              sx={{
                height: 38, px: 2.5, borderRadius: "8px", fontWeight: "bold", textTransform: "none",
                backgroundColor: "#fff", color: NAVY, "&:hover": { backgroundColor: "#e8eefc" },
              }}
            >
              Add Holiday
            </Button>
          </Box>
        </Paper>

        {/* KPI cards */}
        <Box sx={{ display: "grid", gap: 2, mb: 3, gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" } }}>
          <StatCard label="Total Holidays" value={stats.total} icon={CalendarIcon} color="#2563eb" bg="#eff6ff" />
          <StatCard label="Active Holidays" value={stats.active} icon={ActiveIcon} color="#059669" bg="#ecfdf5" />
          <StatCard label="Upcoming" value={stats.upcoming} icon={UpcomingIcon} color="#d97706" bg="#fffbeb" />
          <StatCard label="Holiday Types" value={stats.types} icon={TypeIcon} color="#7c3aed" bg="#f5f3ff" />
        </Box>

        {/* Tabs */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: NAVY },
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
              "& .MuiTab-root.Mui-selected": { color: NAVY },
            }}
          >
            <Tab icon={<CalendarIcon fontSize="small" />} iconPosition="start" label="Calendar View" />
            <Tab icon={<ListIcon fontSize="small" />} iconPosition="start" label="Table View" />
          </Tabs>
        </Paper>

        {/*  Calendar view */}
        {tab === 0 && (
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" } }}>
            <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
              {/* Month header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
                  color: "#fff", py: 2.5, px: 3,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <IconButton onClick={() => goMonth(-1)} sx={{ color: "#fff", backgroundColor: "rgba(255,255,255,0.15)" }}>
                  <ChevronLeftIcon />
                </IconButton>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, letterSpacing: 1.5, opacity: 0.85 }}>{viewYear}</Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: 2 }}>
                    {MONTH_NAMES[viewMonth].toUpperCase()}
                  </Typography>
                </Box>
                <IconButton onClick={() => goMonth(1)} sx={{ color: "#fff", backgroundColor: "rgba(255,255,255,0.15)" }}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {/* Grid */}
              <Box sx={{ p: { xs: 1, sm: 3 } }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 1 }}>
                  {WEEKDAYS.map((d) => (
                    <Typography key={d} sx={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                      {d}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: 1 }}>
                  {monthDays.map((d) => {
                    const key = toKey(d);
                    const inMonth = d.getMonth() === viewMonth;
                    const dayHolidays = holidaysByDate[key] || [];
                    const isHoliday = dayHolidays.length > 0;
                    const isToday = key === todayKey;
                    const isSelected = key === selectedKey;
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const hs = isHoliday ? typeStyle(dayHolidays[0].type) : null;

                    let bg = "transparent";
                    let color = inMonth ? "#1e293b" : "#cbd5e1";
                    if (isWeekend && inMonth) bg = "#f1f5f9";
                    if (isHoliday) { bg = hs.bg; color = hs.color; }
                    if (isToday) { bg = NAVY; color = "#fff"; }

                    return (
                      <Box key={key} sx={{ display: "flex", justifyContent: "center" }}>
                        <Box
                          onClick={() => setSelectedKey(key)}
                          title={dayHolidays.map((h) => h.name).join(", ")}
                          sx={{
                            width: 42, height: 42, borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 14, fontWeight: isToday || isHoliday ? 700 : 500,
                            cursor: "pointer", backgroundColor: bg, color,
                            outline: isSelected ? `2px solid ${isToday ? "#f59e0b" : NAVY}` : "none",
                            outlineOffset: 2,
                            transition: "transform 0.15s",
                            "&:hover": { transform: "scale(1.1)" },
                          }}
                        >
                          {d.getDate()}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Legend */}
                <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap", fontSize: 12, color: "#64748b" }}>
                  {HOLIDAY_TYPES.map((t) => (
                    <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: typeStyle(t).color }} />
                      {t}
                    </Box>
                  ))}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: NAVY }} />
                    Today
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Side panel */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.5 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY }}>
                  {selectedKey === todayKey ? "Today" : fmtDate(selectedKey)}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#d97706" }}>
                  {monthHolidays.length} active holiday{monthHolidays.length === 1 ? "" : "s"} this month
                </Typography>
              </Box>

              <Paper elevation={0} sx={{ ...cardSx, border: "1px dashed #cbd5e1", p: 2.5, mb: 3, textAlign: "center" }}>
                {selectedHolidays.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: "#64748b" }}>
                    No active holiday on this date — regular working day.
                  </Typography>
                ) : (
                  selectedHolidays.map((h) => (
                    <Box key={h.id} sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 700, color: NAVY }}>{h.name}</Typography>
                      <Chip
                        label={h.type}
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 700, fontSize: 11, color: typeStyle(h.type).color, backgroundColor: typeStyle(h.type).bg }}
                      />
                      {h.remarks && (
                        <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.75 }}>{h.remarks}</Typography>
                      )}
                    </Box>
                  ))
                )}
              </Paper>

              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.8, color: "#475569", mb: 1.5 }}>
                ALL ACTIVE HOLIDAYS THIS MONTH
              </Typography>
              {monthHolidays.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No holidays this month.</Typography>
              ) : (
                monthHolidays.map((h) => (
                  <Box
                    key={h.id}
                    onClick={() => setSelectedKey(h.date)}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75, cursor: "pointer", "&:hover": { opacity: 0.75 } }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: typeStyle(h.type).color }} />
                    <Typography sx={{ fontWeight: 700, width: 28, fontSize: 14, color: "#1e293b" }}>
                      {fromKey(h.date).getDate()}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#334155" }}>{h.name}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}

        {/*  Table view */}
        {tab === 1 && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search holiday name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="all">All Types</MenuItem>
                  {HOLIDAY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer sx={{ maxHeight: 520, borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["Date", "Holiday", "Type", "Status", "Remarks", "Actions"].map((h) => (
                      <TableCell
                        key={h}
                        align={h === "Actions" || h === "Status" ? "center" : "left"}
                        sx={{ fontWeight: 700, color: "#fff", backgroundColor: "#154c79", fontSize: 12 }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        No holidays found.
                      </TableCell>
                    </TableRow>
                  )}
                  {tableRows.map((h) => (
                    <TableRow key={h.id} hover>
                      <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{fmtDate(h.date)}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{h.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={h.type}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: 11, color: typeStyle(h.type).color, backgroundColor: typeStyle(h.type).bg }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={h.active ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: 11,
                            color: h.active ? "#15803d" : "#64748b",
                            backgroundColor: h.active ? "#dcfce7" : "#f1f5f9",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, color: "#64748b" }}>{h.remarks || "—"}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <IconButton size="small" onClick={() => openEdit(h)} sx={{ color: "#3b82f6" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteTarget(h)} sx={{ color: "#ef4444" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/*  Add / Edit dialog*/}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {form.id ? "Edit Holiday" : "Add Holiday"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={form.date ? fromKey(form.date) : null}
              onChange={(d) => setField("date", d && !isNaN(d.getTime()) ? toKey(d) : "")}
              format="yyyy/MM/dd"
              slotProps={{
                textField: {
                  size: "small", fullWidth: true, required: true,
                  error: !!formErrors.date, helperText: formErrors.date,
                },
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Holiday Name" size="small" fullWidth required
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            error={!!formErrors.name} helperText={formErrors.name}
          />
          <FormControl size="small" fullWidth required error={!!formErrors.type}>
            <InputLabel>Holiday Type</InputLabel>
            <Select value={form.type} label="Holiday Type" onChange={(e) => setField("type", e.target.value)}>
              {HOLIDAY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
            {formErrors.type && (
              <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5, ml: 1.75 }}>{formErrors.type}</Typography>
            )}
          </FormControl>
          <TextField
            label="Remarks" size="small" fullWidth multiline minRows={2}
            value={form.remarks}
            onChange={(e) => setField("remarks", e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(e) => setField("active", e.target.checked)} color="success" />}
            label={form.active ? "Active" : "Inactive"}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ backgroundColor: NAVY, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#0e3c66" } }}
          >
            {form.id ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/*  Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Delete Holiday</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#64748b" }}>
            {deleteTarget ? `Delete "${deleteTarget.name}" (${fmtDate(deleteTarget.date)})? This cannot be undone.` : ""}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleDelete} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ backgroundColor: "#dc2626", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#b91c1c" } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: "8px", fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HolidayCalendar;